import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  X,
  LayoutDashboardIcon,
  Inbox,
  Users,
  Settings,
  ChevronDown,
  ChevronRight,
  Building2,
  Shield,
  KeyRound,
  Package,
  FileSearch,
  Lock,
  UserCog,
  Activity,
  FileText,
} from 'lucide-react'
import { NavigationItem } from '../types'
import UserMenu from './UserMenu'
import { useThemeStore } from '../stores/themeStore'

interface SidebarProps {
  navigation: NavigationItem[][]
  open: boolean
  setOpen: (open: boolean) => void
  currentPath: string
}

const Sidebar = ({ navigation, open, setOpen, currentPath }: SidebarProps) => {
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  const { isDarkMode } = useThemeStore()

  const toggleExpanded = (label: string) => {
    setExpandedItems(prev =>
      prev.includes(label)
        ? prev.filter(item => item !== label)
        : [...prev, label]
    )
  }

  const getIcon = (iconName: string) => {
    const icons: { [key: string]: React.ComponentType<any> } = {
      dashboard: LayoutDashboardIcon,
      projects: FileSearch,
      inbox: Inbox,
      users: Users,
      settings: Settings,
      tenants: Building2,
      roles: Shield,
      permissions: KeyRound,
      assets: Package,
      security: Lock,
      userManagement: UserCog,
      activity: Activity,
      blogs: FileText,
    }
    return icons[iconName] || LayoutDashboardIcon
  }

  const renderNavigationItem = (item: NavigationItem, level: number = 0) => {
    const Icon = getIcon(item.icon || '')
    const isExpanded = expandedItems.includes(item.label)
    const isActive =
      item.to === currentPath ||
      (item.children && item.children.some(child => child.to === currentPath))

    if (item.children) {
      return (
        <div key={item.label}>
          <button
            onClick={() => toggleExpanded(item.label)}
            className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive
              ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/20 dark:text-primary-400'
              : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
              }`}
          >
            <div className="flex items-center space-x-3">
              <Icon className="h-5 w-5" />
              <span>{item.label}</span>
            </div>
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {isExpanded && (
            <div className="ml-6 mt-1 space-y-1">
              {item.children.map(child =>
                renderNavigationItem(child, level + 1)
              )}
            </div>
          )}
        </div>
      )
    }

    if (item.target === '_blank') {
      return (
        <a
          key={item.label}
          href={item.to}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive
            ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/20 dark:text-primary-400'
            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
            }`}
        >
          <Icon className="h-5 w-5 mr-3" />
          <span>{item.label}</span>
          {item.badge && (
            <span className="ml-auto inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
              {item.badge}
            </span>
          )}
        </a>
      )
    }

    return (
      <Link
        key={item.label}
        to={item.to || '#'}
        onClick={item.onSelect}
        className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive
          ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/20 dark:text-primary-400'
          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
          }`}
      >
        <Icon className="h-5 w-5 mr-3" />
        <span>{item.label}</span>
        {item.badge && (
          <span className="ml-auto inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
            {item.badge}
          </span>
        )}
      </Link>
    )
  }

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
        ${open ? 'translate-x-0' : '-translate-x-full'}
      `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <img
                src={isDarkMode ? "/logo2.png" : "/logo-light.png"}
                alt="Logo"
                className="h-8 w-auto"
              />
            </div>
            <button
              onClick={() => setOpen(false)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigation.map((group, groupIndex) => (
              <div key={groupIndex} className="space-y-1">
                {group.map(item => renderNavigationItem(item))}
                {groupIndex < navigation.length - 1 && (
                  <div className="my-4 border-t border-gray-200 dark:border-gray-700" />
                )}
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <UserMenu />
          </div>
        </div>
      </div>
    </>
  )
}

export default Sidebar
